import React, { useEffect, useState } from "react";
import {
  TextField,
  IconButton,
  Stack,
  Box,
  InputAdornment,
} from "@mui/material";
import { Clear as ClearIcon } from "@mui/icons-material";
import CustomButton from "components/common/CustomButton";

interface FileSearchProps {
  onSearch: (searchTerm: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  initialValue?: string;
}

const FileSearch: React.FC<FileSearchProps> = ({
  onSearch,
  placeholder = "Search files and folders...",
  disabled = false,
  size = "medium",
  fullWidth = true,
  initialValue = "",
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    setSearchTerm(initialValue);
  }, [initialValue]);

  const handleSearch = () => {
    setIsSearching(true);
    const term = searchTerm.trim() || null;
    onSearch(term);
    setIsSearching(false);
  };

  const handleClear = () => {
    setSearchTerm("");
    onSearch(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const getTextFieldSize = () => {
    switch (size) {
      case "small":
        return "small";
      case "large":
        return "medium";
      default:
        return "medium";
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        fullWidth={fullWidth}
        variant="outlined"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        size={getTextFieldSize()}
        InputProps={{
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClear}
                edge="end"
                disabled={disabled}
                sx={{ mr: 0.2 }}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "40px",
            boxSizing: "border-box",
            padding: 0,
          },
          "& .MuiOutlinedInput-input": {
            padding: "0 12px",
            lineHeight: "normal",
            boxSizing: "border-box",
          },
        }}
      />
      <Box>
        <CustomButton
          onClick={handleSearch}
          disabled={disabled || isSearching}
          sx={{
            height: "40px",
            minWidth: "80px",
            padding: "0 12px",
            borderRadius: 0,
            boxSizing: "border-box",
            lineHeight: "normal",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
            "&:disabled": {
              backgroundColor: "action.disabledBackground",
              color: "action.disabled",
            },
          }}
        >
          Search
        </CustomButton>
      </Box>
    </Stack>
  );
};

export default FileSearch;
